import { useRouter } from "next/router"
import { useEffect, useState } from "react"

/**
 * A custom React hook that provides a navigation status.
 * It monitors the route changes in a Next.js application to determine
 * if a navigation process is currently ongoing. This can be used to disable
 * interactive elements like buttons during route transitions to prevent
 * multiple submissions or unintended actions while the route is changing.
 * Usefull for network throttling for example.
 *
 * @returns {boolean} isNavigating - True if a route change is in progress, false otherwise.
 */
export function useIsNavigating(): boolean {
    const router = useRouter()
    const [isNavigating, setIsNavigating] = useState(false)

    useEffect(() => {
        const handleRouteChangeStart = () => setIsNavigating(true)
        const handleRouteChangeComplete = () => setIsNavigating(false)
        const handleRouteChangeError = () => setIsNavigating(false)

        router.events.on("routeChangeStart", handleRouteChangeStart)
        router.events.on("routeChangeComplete", handleRouteChangeComplete)
        router.events.on("routeChangeError", handleRouteChangeError)

        return () => {
            router.events.off("routeChangeStart", handleRouteChangeStart)
            router.events.off("routeChangeComplete", handleRouteChangeComplete)
            router.events.off("routeChangeError", handleRouteChangeError)
        }
    }, [router])

    return isNavigating
}
